import { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { SEO } from '@/components/common/SEO';
import { Hero } from '@/components/common/Hero';
import { useCreateLead } from '@/hooks/useLeads';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { useTranslation } from '@/features/i18n';
import { Link } from 'react-router-dom';

type ContactFormData = z.infer<ReturnType<typeof createContactSchema>>;

function createContactSchema(t: ReturnType<typeof useTranslation>['t']) {
  return z.object({
    name: z.string().min(2, t.contact.form.validation?.nameMin || 'Name must be at least 2 characters'),
    email: z.string().email(t.contact.form.validation?.emailInvalid || 'Invalid email address'),
    phone: z.string().optional(),
    company: z.string().optional(),
    company_size: z.string().optional(),
    service_interest: z.string().optional(),
    message: z.string().min(10, t.contact.form.validation?.messageMin || 'Message must be at least 10 characters'),
  });
}

export function ContactPage() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { data: settings } = useSiteSettings();
  const createLead = useCreateLead();
  const { t } = useTranslation();

  const serviceOptions = [
    { value: 'crm', label: t.nav.services.crm },
    { value: 'app', label: t.nav.services.app },
    { value: 'web', label: t.nav.services.web },
    { value: 'sms-marketing', label: t.nav.services.sms },
    { value: 'voice-agent', label: t.nav.services.voice },
    { value: 'other', label: t.contact.form.other || 'Other' },
  ];

  const companySizeOptions = [
    { value: '1-10', label: t.contact.form.companySize?.small || '1-10 employees' },
    { value: '11-50', label: t.contact.form.companySize?.medium || '11-50 employees' },
    { value: '51-200', label: t.contact.form.companySize?.large || '51-200 employees' },
    { value: '200+', label: t.contact.form.companySize?.enterprise || '200+ employees' },
  ];

  const contactSchema = createContactSchema(t);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
  });

  const onSubmit = async (data: ContactFormData) => {
    try {
      await createLead.mutateAsync({
        name: data.name,
        email: data.email,
        phone: data.phone || null,
        company: data.company || null,
        company_size: data.company_size || null,
        service_interest: data.service_interest || null,
        message: data.message,
        source: 'website',
        utm_source: null,
        utm_medium: null,
        utm_campaign: null,
        notes: null,
        assigned_to: null,
        contacted_at: null,
      });
      setIsSubmitted(true);
      reset();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  if (isSubmitted) {
    return (
      <>
        <SEO title={t.contact.success.title} />
        <section className="min-h-screen flex items-center justify-center bg-[#edf1f5] relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#0078ff]/5 rounded-full blur-3xl" />
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative text-center max-w-md mx-auto px-4"
          >
            <div className="w-20 h-20 rounded-full bg-[#0078ff]/10 flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="w-10 h-10 text-[#0078ff]" />
            </div>
            <h1 className="text-[32px] md:text-[40px] font-semibold text-black tracking-[-0.02em]">
              {t.contact.success.title}
            </h1>
            <p className="mt-4 text-lg text-[#666]">
              {t.contact.success.description}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => setIsSubmitted(false)}
                className="ninito-btn ninito-btn-primary"
              >
                {t.contact.form.sendNew}
              </button>
              <Link to="/" className="ninito-btn ninito-btn-outline">
                Tilbake til forsiden
              </Link>
            </div>
          </motion.div>
        </section>
      </>
    );
  }

  return (
    <>
      <SEO
        title={t.contact.title}
        description={t.contact.description}
      />

      <Hero
        title={t.contact.title}
        subtitle={t.contact.subtitle}
        description={t.contact.description}
        size="md"
        variant="light"
      />

      <section className="ninito-section bg-[#edf1f5]">
        <div className="ninito-container">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
            {/* Contact info */}
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-semibold text-black tracking-tight">
                {t.contact.info.title}
              </h2>

              <div className="mt-8 space-y-6">
                {settings?.contact_email && (
                  <a
                    href={`mailto:${settings.contact_email}`}
                    className="flex items-center group"
                  >
                    <div className="w-12 h-12 rounded-2xl bg-[#0078ff]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#0078ff]/20 transition-colors">
                      <Mail className="w-5 h-5 text-[#0078ff]" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-sm font-medium text-[#666]">{t.contact.info.email}</h3>
                      <p className="text-black group-hover:text-[#0078ff] transition-colors">
                        {settings.contact_email}
                      </p>
                    </div>
                  </a>
                )}

                {settings?.contact_phone && (
                  <a
                    href={`tel:${settings.contact_phone.replace(/\s/g, '')}`}
                    className="flex items-center group"
                  >
                    <div className="w-12 h-12 rounded-2xl bg-[#0078ff]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#0078ff]/20 transition-colors">
                      <Phone className="w-5 h-5 text-[#0078ff]" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-sm font-medium text-[#666]">{t.contact.info.phone}</h3>
                      <p className="text-black group-hover:text-[#0078ff] transition-colors">
                        {settings.contact_phone}
                      </p>
                    </div>
                  </a>
                )}

                {settings?.contact_address && (
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-2xl bg-[#0078ff]/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-[#0078ff]" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-sm font-medium text-[#666]">{t.contact.info.address}</h3>
                      <p className="text-black">
                        {settings.contact_address}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-12 pt-8 border-t border-black/10">
                <h3 className="font-medium text-black">
                  {t.contact.info.hours.title}
                </h3>
                <div className="mt-4 space-y-2 text-[#666]">
                  <p>{t.contact.info.hours.weekdays}</p>
                  <p>{t.contact.info.hours.weekend}</p>
                </div>
              </div>
            </div>

            {/* Contact form */}
            <div className="lg:col-span-3">
              <div className="ninito-card">
                <h2 className="text-2xl font-semibold text-black tracking-tight mb-8">
                  {t.contact.formTitle}
                </h2>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-[#666] mb-2">
                        {t.contact.form.name}
                      </label>
                      <input
                        type="text"
                        placeholder={t.contact.form.namePlaceholder}
                        className="ninito-input"
                        {...register('name')}
                      />
                      {errors.name && (
                        <p className="mt-2 text-sm text-red-500">{errors.name.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#666] mb-2">
                        {t.contact.form.email}
                      </label>
                      <input
                        type="email"
                        placeholder={t.contact.form.emailPlaceholder}
                        className="ninito-input"
                        {...register('email')}
                      />
                      {errors.email && (
                        <p className="mt-2 text-sm text-red-500">{errors.email.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-[#666] mb-2">
                        {t.contact.form.phone}
                      </label>
                      <input
                        type="tel"
                        placeholder={t.contact.form.phonePlaceholder}
                        className="ninito-input"
                        {...register('phone')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#666] mb-2">
                        {t.contact.form.company}
                      </label>
                      <input
                        type="text"
                        placeholder={t.contact.form.companyPlaceholder}
                        className="ninito-input"
                        {...register('company')}
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-[#666] mb-2">
                        {t.contact.form.companySize.label}
                      </label>
                      <select
                        className="ninito-input appearance-none cursor-pointer"
                        {...register('company_size')}
                      >
                        <option value="">{t.contact.form.companySize.placeholder}</option>
                        {companySizeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#666] mb-2">
                        {t.contact.form.service}
                      </label>
                      <select
                        className="ninito-input appearance-none cursor-pointer"
                        {...register('service_interest')}
                      >
                        <option value="">{t.contact.form.servicePlaceholder}</option>
                        {serviceOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#666] mb-2">
                      {t.contact.form.message}
                    </label>
                    <textarea
                      placeholder={t.contact.form.messagePlaceholder}
                      rows={5}
                      className="ninito-input resize-none"
                      {...register('message')}
                    />
                    {errors.message && (
                      <p className="mt-2 text-sm text-red-500">{errors.message.message}</p>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4">
                    <p className="text-sm text-[#666]">
                      {t.contact.form.required}
                    </p>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="ninito-btn ninito-btn-primary ninito-btn-lg group disabled:opacity-50"
                    >
                      {isSubmitting ? 'Sender...' : t.contact.form.submit}
                      <Send className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
